import React, { FC, useState, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { defineMessages, useIntl } from "react-intl";
import { useSnackbar } from "@myplant-io/snackbar";
import { useAppState } from "../../state/App";
import { GroupedChatHistory, FAVORITES_GROUP } from "./ChatHistoryList";
import { useConversationDelete } from "../../hooks/useConversationDelete";
import { useConversationUpdate } from "../../hooks/useConversationUpdate";
import { useConversationFavorite } from "../../hooks/useConversationFavorite";
import { commonMessages } from "../../commonMessages";
import { Timestamp } from "../common/Timestamp";
import { Conversation } from "../../types";

interface ChatHistoryListItemCellProps {
  item: Conversation;
  onSelect: (item: Conversation | null) => void;
}

const messages = defineMessages({
  deleteConfirmMessage: {
    id: "chatHistory.deleteConfirm.message",
    defaultMessage:
      "The history of this chat session will be permanently removed.",
  },
});

interface ChatHistoryListItemGroupsProps {
  groupedChatHistory: GroupedChatHistory[];
}

const formatMonth = (month: string) => {
  if (month === FAVORITES_GROUP) return month;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const [monthName, yearString] = month.split(" ");
  const year = parseInt(yearString);

  if (year === currentYear) {
    return monthName;
  } else {
    return month;
  }
};

export const ChatHistoryListItemCell: FC<ChatHistoryListItemCellProps> = ({
  item,
  onSelect,
}) => {
  const { formatMessage: t } = useIntl();
  const { showInfoMessage } = useSnackbar();
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [edit, setEdit] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [errorDelete, setErrorDelete] = useState<string | undefined>(undefined);
  const [renameLoading, setRenameLoading] = useState(false);
  const [errorRename, setErrorRename] = useState<string | undefined>(undefined);
  const { mutateAsync: deleteConversation } = useConversationDelete();
  const { mutateAsync: updateConversation, isLoading: isUpdating } =
    useConversationUpdate();
  const { mutateAsync: toggleFavorite } = useConversationFavorite();

  const {
    state: { currentConversationId, isGenerating },
    actions: {
      setCurrentConversationId,
      setMessages,
      setCurrentTTSUrl,
      setIsPlaying,
      setConversationToStop,
    },
  } = useAppState();
  const isSelected = item?._id === currentConversationId;
  const isItemGenerating = item.is_generating || (isSelected && isGenerating);

  useEffect(() => {
    if (!isSelected) setMenuAnchor(null);
  }, [isSelected]);

  // Restore focus to the row after it remounts in a new list position
  // (e.g. after a favorite toggle moves the item between groups). We only
  // grab focus when mounting while selected AND when nothing meaningful
  // already has focus (body / null = focus was lost during the re-render).
  useEffect(() => {
    if (isSelected) {
      const active = document.activeElement;
      if (!active || active === document.body) {
        rowRef.current?.focus();
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async () => {
    try {
      await deleteConversation(item._id);
      // Stop any active generation for this conversation (foreground or background).
      setConversationToStop(item._id);
      if (item._id === currentConversationId) {
        setCurrentConversationId(undefined);
        setMessages([]);
      }
      setIsHovered(false);
      setMenuAnchor(null);
    } catch (e) {
      setErrorDelete("Error: could not delete item");
    }
  };

  const onEdit = () => {
    setMenuAnchor(null);
    setEdit(true);
    setEditTitle(item?.title);
  };

  const handleSelectItem = () => {
    onSelect(item);
    setCurrentTTSUrl(undefined);
    setIsPlaying(false);
    setCurrentConversationId(item._id);
  };

  const handleSaveEdit = async () => {
    if (errorRename || renameLoading) {
      return;
    }

    if (editTitle == item.title) {
      setErrorRename("Error: Enter a new title to proceed.");
      setTimeout(() => {
        setErrorRename(undefined);
      }, 5000);
      return;
    }

    try {
      setRenameLoading(true);
      await updateConversation({
        id: item._id,
        title: editTitle,
      });
      setErrorRename(undefined);
      setEditTitle("");
      setEdit(false);
    } catch (e) {
      setErrorRename("Error: could not rename item");
    } finally {
      setRenameLoading(false);
    }
  };

  const chatHistoryTitleOnChange:
    | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined = (e) => {
    setEditTitle(e.target.value);
  };

  const cancelEditTitle = () => {
    setEdit(false);
    setEditTitle("");
  };

  const handleKeyPressEdit:
    | React.KeyboardEventHandler<HTMLDivElement>
    | undefined = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      return handleSaveEdit();
    }
    if (e.key === "Escape") {
      cancelEditTitle();
      return;
    }
  };

  return (
    <div
      ref={rowRef}
      role="button"
      key={item._id}
      tabIndex={0}
      aria-label="chat history item"
      className="px-4 py-2"
      onClick={() => {
        handleSelectItem();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          if (e.target !== e.currentTarget) return;
          e.preventDefault();
          handleSelectItem();
        }
      }}
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setIsHovered(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setIsHovered(false);
      }}
      onBlur={() => setIsHovered(false)}
      style={{
        backgroundColor:
          isSelected || isHovered ? theme.palette.action.hover : "transparent",
        cursor: isItemGenerating ? "default" : "pointer",
      }}
    >
      {edit ? (
        <div className="w-full">
          <form
            aria-label="edit title form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveEdit();
            }}
          >
            <div className="flex w-full justify-between items-center">
              <div>
                <TextField
                  size="small"
                  autoFocus
                  value={editTitle}
                  placeholder={item.title}
                  onChange={chatHistoryTitleOnChange}
                  onKeyDown={handleKeyPressEdit}
                  disabled={isUpdating || errorRename ? true : false}
                  error={Boolean(errorRename)}
                  helperText={errorRename}
                />
              </div>
              {editTitle && (
                <div className="flex">
                  <IconButton
                    role="button"
                    disabled={isUpdating || errorRename !== undefined}
                    onKeyDown={(e) =>
                      e.key === " " || e.key === "Enter"
                        ? handleSaveEdit()
                        : null
                    }
                    onClick={handleSaveEdit}
                    aria-label="confirm new title"
                    color="success"
                    size="small"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    role="button"
                    disabled={isUpdating || errorRename !== undefined}
                    onKeyDown={(e) =>
                      e.key === " " || e.key === "Enter"
                        ? cancelEditTitle()
                        : null
                    }
                    onClick={() => cancelEditTitle()}
                    aria-label="cancel edit title"
                    color="error"
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-between items-center w-full min-h-8">
          <div className="truncate">
            <div className="flex items-center">
              {isItemGenerating && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 0.75,
                    flexShrink: 0,
                    gap: "3px",
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: "text.secondary",
                        animation: "typingBounce 1.2s ease-in-out infinite",
                        animationDelay: `${i * 0.2}s`,
                        "@keyframes typingBounce": {
                          "0%, 80%, 100%": {
                            transform: "scale(0.6)",
                            opacity: 0.4,
                          },
                          "40%": { transform: "scale(1)", opacity: 1 },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              <Typography noWrap>{item?.title}</Typography>
            </div>
            {isSelected && item.updated && (
              <Timestamp date={item.updated} noWrap sx={{ lineHeight: 1 }} />
            )}
          </div>
          {isSelected && (
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <IconButton
                id={`more-actions-btn-${item._id}`}
                size="small"
                aria-label={t(commonMessages.moreActions)}
                aria-haspopup="menu"
                aria-controls={
                  menuAnchor ? `more-actions-menu-${item._id}` : undefined
                }
                aria-expanded={Boolean(menuAnchor)}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                id={`more-actions-menu-${item._id}`}
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{
                  "aria-labelledby": `more-actions-btn-${item._id}`,
                }}
              >
                <MenuItem
                  onClick={async () => {
                    try {
                      await toggleFavorite(item._id);
                    } finally {
                      setMenuAnchor(null);
                    }
                  }}
                >
                  <ListItemIcon>
                    {item.is_favorite ? (
                      <StarIcon fontSize="small" color="warning" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      item.is_favorite
                        ? t(commonMessages.removeFromFavorites)
                        : t(commonMessages.addToFavorites)
                    }
                  />
                </MenuItem>
                <MenuItem onClick={onEdit}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t(commonMessages.edit)} />
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    showInfoMessage({
                      message: t(messages.deleteConfirmMessage),
                      action: t(commonMessages.delete),
                      handleAction: onDelete,
                    });
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(commonMessages.delete)}
                    primaryTypographyProps={{ color: "error" }}
                  />
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      )}
      {errorDelete && (
        <Typography variant="caption" color="error">
          {errorDelete}
        </Typography>
      )}
    </div>
  );
};

export const ChatHistoryListItemGroups: React.FC<
  ChatHistoryListItemGroupsProps
> = ({ groupedChatHistory }) => {
  const [, setSelectedItem] = useState<Conversation | null>(null);

  const handleSelectHistory = (item?: Conversation) => {
    if (item) {
      setSelectedItem(item);
    }
  };

  return (
    <>
      {groupedChatHistory.map(
        (group) =>
          group.entries.length > 0 && (
            <div key={group.month}>
              <div>
                <Typography>{formatMonth(group.month)}</Typography>
              </div>
              {group.entries.map((entry) => (
                <ChatHistoryListItemCell
                  key={entry._id}
                  item={entry}
                  onSelect={() => handleSelectHistory(entry)}
                />
              ))}
              <Divider className="my-4" />
            </div>
          )
      )}
    </>
  );
};
